import React, { useContext, useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import { API_BASE } from '../api';

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [contacts, setContacts] = useState([]);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [showAiModal, setShowAiModal] = useState(false);
  const [chatLoading, setChatLoading] = useState(true);
  const [botInput, setBotInput] = useState('');
  const [botSending, setBotSending] = useState(false);
  const [botError, setBotError] = useState('');
  const [liveReplies, setLiveReplies] = useState([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setContactsLoading(true);
      try {
        const snap = await getDocs(query(collection(db, 'contacts'), orderBy('createdAt', 'desc')));
        setContacts(snap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.() })));
      } catch (e) { console.error('Load contacts:', e); } finally { setContactsLoading(false); }
    })();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setChatLoading(true);
      try {
        const snap = await getDocs(query(collection(db, 'chat_messages'), orderBy('createdAt', 'desc'), limit(100)));
        setChatMessages(snap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.() })));
      } catch (e) { console.error('Load AI messages:', e); } finally { setChatLoading(false); }
    })();
  }, [user]);

  const sendToBot = async () => {
    const text = botInput.trim();
    if (!text || botSending) return;
    setBotError('');
    setBotInput('');
    setBotSending(true);
    const userMsg = { role: 'user', content: text };
    setLiveReplies((prev) => [...prev, userMsg]);
    try {
      const res = await fetch(`${API_BASE}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [userMsg], max_tokens: 256 }),
      });
      const data = await res.json();
      if (!res.ok) {
        setBotError(data.error || `Error ${res.status}`);
        setLiveReplies((prev) => prev.slice(0, -1));
        return;
      }
      setLiveReplies((prev) => [...prev, { role: 'assistant', content: data.content || '' }]);
    } catch (e) {
      setBotError(e.message || 'Request failed');
      setLiveReplies((prev) => prev.slice(0, -1));
    } finally {
      setBotSending(false);
    }
  };

  if (!user) return <div className="flex items-center justify-center h-screen"><p className="text-gray-600">Please log in to access the dashboard</p></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <nav className="bg-white shadow-md">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">DriscollCo Dashboard</h1>
          <button onClick={() => { logout(); window.location.href = '/'; }} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">Logout</button>
        </div>
      </nav>
      <div className="container mx-auto px-6 py-10">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Welcome, {user.firstName || user.email}!</h2>
          <p className="text-gray-600 mb-6">Email: <span className="font-semibold">{user.email}</span></p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white"><h3 className="text-xl font-bold mb-2">Properties</h3><p className="text-3xl font-bold">0</p><p className="text-blue-100 mt-2">Listed properties</p></div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white"><h3 className="text-xl font-bold mb-2">Contacts</h3><p className="text-3xl font-bold">{contactsLoading ? '…' : contacts.length}</p><p className="text-green-100 mt-2">Form submissions</p></div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white"><h3 className="text-xl font-bold mb-2">Registered Users</h3><p className="text-3xl font-bold">—</p><p className="text-purple-100 mt-2">Total users</p></div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button className="bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-medium">+ Add Property</button>
            <button onClick={() => setShowContactsModal(true)} className="bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium">View Contacts</button>
            <button onClick={() => setShowAiModal(true)} className="bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 font-medium">View AI Q&A</button>
            <button className="bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 font-medium">View All Properties</button>
            <button className="bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 font-medium">Account Settings</button>
          </div>
        </div>
      </div>
      {showContactsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b"><h3 className="text-xl font-bold text-gray-800">Contact Submissions</h3><button onClick={() => setShowContactsModal(false)} className="text-gray-500 text-2xl">×</button></div>
            <div className="p-4 overflow-y-auto flex-1">
              {contactsLoading ? <p className="text-gray-500">Loading…</p> : contacts.length === 0 ? <p className="text-gray-500">No contact submissions yet.</p> : (
                <ul className="space-y-4">{contacts.map((c) => <li key={c.id} className="border-b border-gray-200 pb-4 last:border-0"><p className="font-semibold text-gray-800">{c.name}</p><p className="text-sm text-gray-600">{c.email}</p><p className="text-gray-700 mt-1">{c.message}</p>{c.createdAt && <p className="text-xs text-gray-400 mt-2">{c.createdAt.toLocaleString?.() || String(c.createdAt)}</p>}</li>)}</ul>
              )}
            </div>
          </div>
        </div>
      )}

      {showAiModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b"><h3 className="text-xl font-bold text-gray-800">DC Bot (AI Q&A)</h3><button onClick={() => setShowAiModal(false)} className="text-gray-500 text-2xl">×</button></div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {chatLoading ? <p className="text-gray-500">Loading…</p> : null}
              {chatMessages.length > 0 && (
                <ul className="space-y-3 mb-4">
                  {chatMessages.map((m) => (
                    <li key={m.id} className={`rounded-lg p-3 ${m.role === 'user' ? 'bg-indigo-50 ml-8' : 'bg-amber-50 mr-8'}`}>
                      <span className="text-xs font-semibold text-gray-500 uppercase">{m.role}</span>
                      <p className="text-gray-800 mt-1">{m.content}</p>
                      {m.createdAt && <p className="text-xs text-gray-400 mt-2">{m.createdAt.toLocaleString?.() || String(m.createdAt)}</p>}
                    </li>
                  ))}
                </ul>
              )}
              {liveReplies.length > 0 && (
                <ul className="space-y-3 mb-4">
                  {liveReplies.map((m, i) => (
                    <li key={`live-${i}`} className={`rounded-lg p-3 ${m.role === 'user' ? 'bg-indigo-50 ml-8' : 'bg-amber-50 mr-8'}`}>
                      <span className="text-xs font-semibold text-gray-500 uppercase">{m.role}</span>
                      <p className="text-gray-800 mt-1">{m.content}</p>
                    </li>
                  ))}
                </ul>
              )}
              {!chatLoading && chatMessages.length === 0 && liveReplies.length === 0 && <p className="text-gray-500 mb-4">Ask the DC bot anything. Type below and press Send.</p>}
              {botError && <p className="text-red-600 text-sm mb-4">{botError}</p>}
            </div>
            <div className="p-4 border-t flex gap-2">
              <input
                type="text"
                value={botInput}
                onChange={(e) => setBotInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendToBot()}
                placeholder="Type a message…"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                disabled={botSending}
              />
              <button onClick={sendToBot} disabled={botSending || !botInput.trim()} className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium">Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
